import { inject, EnvironmentInjector, runInInjectionContext } from '@angular/core';
import { ActivatedRouteSnapshot, ResolveFn } from '@angular/router';
import { forkJoin, of, switchMap } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  BlogPostService,
  BlogPostPopulated,
  ApiResponse,
  PaginatedResponse,
  PostReactionService,
  PostReactionsResponse,
  SeriesService,
  SeriesNavContext,
} from 'cms-post-lib';
import { environment } from '../../../environments/environment';

export interface PostDetailResolvedData {
  post: BlogPostPopulated | null;
  latestPosts: BlogPostPopulated[];
  reactions: PostReactionsResponse['data'] | null;
  seriesContext: SeriesNavContext[];
}

export const postDetailResolver: ResolveFn<PostDetailResolvedData> = (
  route: ActivatedRouteSnapshot
) => {
  const injector = inject(EnvironmentInjector);
  const slugOrId = route.paramMap.get('slugOrId') ?? '';

  return runInInjectionContext(injector, () => {
    const blogPostService = inject(BlogPostService);
    const reactionService = inject(PostReactionService);
    const seriesService = inject(SeriesService);

    return blogPostService.getPublicBlogPost(slugOrId).pipe(
      catchError(() => of({ success: false, data: undefined } as ApiResponse<BlogPostPopulated>)),
      switchMap(postResponse => {
        const post = postResponse.data ?? null;

        const latestPosts$ = blogPostService
          .getPublicBlogPosts({ orgId: environment.orgId, page: 1, limit: 5 })
          .pipe(
            catchError(() =>
              of({ success: false, data: [], count: 0, pagination: null } as unknown as PaginatedResponse<BlogPostPopulated>)
            )
          );

        // Fetch reactions if we have a post id; results populate the service
        // signal map via the tap() inside getReactions().
        const reactions$ = post
          ? reactionService
              .getReactions(post._id, environment.orgId)
              .pipe(
                map(r => r.data),
                catchError(() => of(null))
              )
          : of(null);

        const seriesContext$ = post
          ? seriesService
              .getSeriesForPublicPost(post.slug ?? post._id)
              .pipe(
                map(r => r.data ?? []),
                catchError(() => of([] as SeriesNavContext[]))
              )
          : of([] as SeriesNavContext[]);

        return forkJoin({ latestResponse: latestPosts$, reactionsData: reactions$, seriesContextData: seriesContext$ }).pipe(
          map(({ latestResponse, reactionsData, seriesContextData }) => ({
            post,
            latestPosts: latestResponse.data ?? [],
            reactions: reactionsData,
            seriesContext: seriesContextData,
          }))
        );
      })
    );
  });
};
